<template>
  <div class="rvr-stand-configurator">
    <h4>{{  t.choose_place_setup }}</h4>
    <div v-for="(reservation, id) in reservations" :key="id">
      <CollapsibleStandSection 
        :id="id" 
        section_class="collapsible-stand-section" 
        :placeSize="getPlaceSizeById(reservation.placeSizeId)"
        :isConfigured="isStandConfigured(reservation)"
      >
      <form>
        <div class="form-field">
          <label :for="`want-tent-${id}`">
            <span class="rvr-label-holder">{{ t.want_tent }}</span>
            <div class="form-field">
              <RadioButton :options="[{value: 'yes', label: t.yes}, {value: 'no', label: t.no}]" :name="`want-tent-${id}`" v-model="reservation.wantsTent" />
            </div>
          </label>
        </div>

        <div class="form-field" v-if="reservation.wantsTent === 'yes'">
          <label :for="`tent-${id}`">
            <span class="rvr-label-holder">{{ t.choose_tent }}
              <span class="rvr-label-meta">{{ t.tents_setup_default }} <strong>({{ t.tents_setup_default_meta }})</strong></span>
            </span>
            <DropDown
              :options="filteredTentsToOptions(getPlaceSizeById(reservation.placeSizeId).size, true)"
              :name="`tent-${id}`"
              v-model="reservation.tentId"
              :loading="tentsLoading"
            />
          </label>
        </div>

        <div class="form-field" v-if="reservation.tentId > 1 && !tentFitsExactly(reservation)">
          <label :for="`placement-${id}`">
            <span class="rvr-label-holder">{{ t.tent_placement }}</span>
            <DropDown
                :options="tentPlacementsToOptions()"
                :name="`placement-${id}`"
                v-model="reservation.placement"
            />
          </label>
        </div>

        <div class="form-field">
          <label :for="`flagpost-${id}`">
            <span class="rvr-label-holder">{{ t.flagpost }}
            </span>
            <div class="form-field">
              <RadioButton :options="[{value: 'yes', label: t.yes}, {value: 'no', label: t.no} ]" :name="`flagpost-${id}`" v-model="reservation.flagpost" />
            </div>
          </label>
        </div>

        <div class="form-field">
            <span class="rvr-label-holder">{{ t.power_connection }}
              <span class="rvr-label-meta">{{ t.power_connection_meta }} <a target="_blank" href="https://vlagtwedderlandbouwbeurs.nl/het-bestuur/">{{ t.power_connection_contact_persons }}</a> <strong>{{ t.power_connection_meta_bold }}</strong></span>
            </span>
        </div>


        <div class="form-field">
          <label :for="`description-${id}`">
            <span class="rvr-label-holder">{{ t.questions }}
            </span>
            <TextArea :name="`description-${id}`" v-model="reservation.description" />
          </label>
        </div>

      </form>
      </CollapsibleStandSection>

    </div>
  </div>
</template>


<script>
import {ref, watch, watchEffect} from "vue";
import useTents from "@/composables/useTents";
import useReservations from "@/composables/useReservations";
import usePlaceSizes from "@/composables/usePlaceSizes";
import CollapsibleStandSection from "@/components/public/helpers/CollapsibleStandSection.vue";
import DropDown from "@/components/shared/FormElements/DropDown.vue";
import useTentPlacements from "@/composables/useTentPlacements";
import useStepNavigation from "@/composables/useStepNavigation";
import TextArea from "@/components/shared/FormElements/TextArea.vue";
import {apiUpdateReservations, apiUpdateReservationTentId} from "@/api/endpoints";
import useMessages from "@/composables/useMessages";
import useSpinner from "@/composables/useSpinner";
import useTexts from "@/composables/useTexts";
import RadioButton from "@/components/shared/FormElements/RadioButton.vue";

export default {
  name: "StandConfigurator",
  components: {RadioButton, TextArea, CollapsibleStandSection, DropDown },
  setup() {
    const DEFAULT_TENT_ID = 'default';
    const NO_TENT_ID = '1'; //Geen tent
    const DEFAULT_TENT_PLACEMENT_ID = 3; //rechts achter in de hoek

    const { reservations } = useReservations();
    const { placeSizes, getPlaceSizeById } = usePlaceSizes();
    const { tentPlacementsToOptions } = useTentPlacements();
    const { tents, filteredTentsToOptions, tentsLoaded, fetchTents, fitsExactlyPlaceSize } = useTents();
    const { nextButtonEnabled, nextButtonPressed, goToNextStep } = useStepNavigation();
    const { showMessage } = useMessages();
    const { showSpinner, hideSpinner} = useSpinner();
    const tentsLoading = ref(false);
    const { getTextsForComponent } = useTexts();
    const t = getTextsForComponent('StandConfigurator');

    // Initialize wantsTent property for each reservation to null (user must make a decision)
    Object.values(reservations).forEach((reservation) => {
      if (!Object.prototype.hasOwnProperty.call(reservation, 'wantsTent')) {
        reservation.wantsTent = null;
      }
    });

    // Add function to check if a stand is configured
    const isStandConfigured = (reservation) => {
      // If no decision has been made, stand is not configured
      if (reservation.wantsTent === null || reservation.wantsTent === undefined) {
        return false;
      }
      
      // If user selected "no", they're done (tentId is automatically set to NO_TENT_ID)
      if (reservation.wantsTent === 'no') {
        return true; // Configured with no tent
      }
      
      // If user selected "yes", check if they've selected a specific tent
      if (reservation.wantsTent === 'yes') {
        return reservation.tentId !== 'default' && reservation.tentId !== null;
      }
      
      // Fallback: not configured
      return false;
    };

    // Check if tent fits exactly on the place (no placement selection needed)
    const tentFitsExactly = (reservation) => {
      // Only check if a valid tent is selected
      if (!reservation.tentId || reservation.tentId === DEFAULT_TENT_ID || reservation.tentId === NO_TENT_ID) {
        return false;
      }
      
      // Find the tent in the tents array
      const tent = tents.value.find(t => t.id == reservation.tentId);
      if (!tent || !tent.size) {
        return false;
      }
      
      // Get the place size
      const placeSize = getPlaceSizeById(reservation.placeSizeId);
      if (!placeSize || !placeSize.size) {
        return false;
      }
      
      // Check if tent dimensions exactly match place dimensions
      return fitsExactlyPlaceSize(tent.size, placeSize.size);
    };

    watch(nextButtonPressed, async (isPressed) => {
      if (isPressed) {
        await updateReservations();
        nextButtonPressed.value = false;
        goToNextStep();
      }
    });

    watch(tentsLoading, (newVal) => {
      if (newVal) {
        showSpinner();
      } else {
        hideSpinner();
      }
    }, { immediate: true });

    const checkTentIds = (reservations) => {
      // Check for reservations that are not fully configured:
      // 1. wantsTent is null/undefined (no decision made yet)
      // 2. wantsTent is 'yes' but tentId is still 'default' or null
      const unconfiguredReservation = Object.values(reservations).find(r => {
        // No decision made yet
        if (r.wantsTent === null || r.wantsTent === undefined) {
          return true;
        }
        // User chose "yes" but hasn't selected a specific tent
        if (r.wantsTent === 'yes' && (r.tentId === DEFAULT_TENT_ID || r.tentId === null)) {
          return true;
        }
        // User chose "no" is fully configured
        return false;
      });

      // Enable the next button if all reservations are configured, disable it otherwise
      nextButtonEnabled.value = !unconfiguredReservation;
    };

// Loop over each reservation object
    Object.entries(reservations).forEach(([markerId, reservation]) => {
      // Create a watcher for each reservation.wantsTent
      watch(
          () => reservation.wantsTent,
          async (newValue) => {
            if (newValue === 'no') {
              // User selected "No" - automatically set to "Geen tent"
              reservation.tentId = NO_TENT_ID;
              reservation.placement = null;
            } else if (newValue === 'yes') {
              // User selected "Yes" - reset to default if was NO_TENT_ID
              if (reservation.tentId === NO_TENT_ID) {
                reservation.tentId = DEFAULT_TENT_ID;
              }
            }
          }
      );

      // Create a watcher for each reservation.tentId
      watch(
          () => reservation.tentId,
          async (newTentId) => {
            tentsLoading.value = true;
            // If the newTentId is DEFAULT_TENT_ID or NO_TENT_ID, nullify the placement
            if (newTentId === DEFAULT_TENT_ID || newTentId === NO_TENT_ID) {
              reservation.placement = null;
            }
            else {
              // Check if tent fits exactly - if so, set placement to null, otherwise use default
              const tent = tents.value.find(t => t.id == newTentId);
              const placeSize = getPlaceSizeById(reservation.placeSizeId);
              if (tent && placeSize && fitsExactlyPlaceSize(tent.size, placeSize.size)) {
                reservation.placement = null;
              } else {
                reservation.placement = DEFAULT_TENT_PLACEMENT_ID;
              }
            }

            await updateReservationTentId(markerId, newTentId);
            await fetchTents();
            tentsLoading.value = false;
          }
      );
    });

    watchEffect(() => checkTentIds(reservations));

    const updateReservationTentId = async (markerId, newTentId) => {
      try {
        let updatedReservation = { markerId: markerId, tent_id: newTentId };
        await apiUpdateReservationTentId(updatedReservation);
      } catch (error) {
        if (error.response && error.response.data.code === 'tent_not_available') {
          showMessage(error.response.data.message);
          reservations[markerId].tentId = 'default';
          await fetchTents();
        }
        console.error('Failed to update reservation:', error);
      }
    };

    const updateReservations = async () => {
      try {
        let updatedReservations = { reservations: reservations };
        await apiUpdateReservations(updatedReservations);
      } catch (error) {
        console.error('Failed to update reservations:', error);
      }
    };


    return {
      placeSizes,
      filteredTentsToOptions,
      tents,
      tentsLoaded,
      getPlaceSizeById,
      reservations,
      tentPlacementsToOptions,
      nextButtonEnabled,
      tentsLoading,
      nextButtonPressed,
      isStandConfigured,
      tentFitsExactly,
      t
    };
  },
};
</script>

<style scoped>
/* Add your CSS styles here */
.rvr-stand-configurator {
  text-align: left;
}
.rvr-stand-configurator .collapsible-stand-section {
  background: #ffffff;
  margin: 0 0 15px 0;
  border-radius: 4px;
}
.form-field {
  margin-bottom: 20px;
}
.rvr-label-holder {
  font-weight: bold;
  font-size: 1.2em;
  width: 100%;
  display: block;
  margin-bottom: 10px;
}

.rvr-label-holder .rvr-label-meta {
  color: #000;
  font-size: 0.8em;
  font-style: italic;
  font-weight: normal;
}
</style>
